import { cssVar, Stack, Box, Text, DataList, DataItem } from '@cad-arc';
import { parseISO } from 'date-fns';
import { ActionTypeEnum, ElasticUserAction } from '@mark43/cad-api';
import { Virtuoso } from 'react-virtuoso';
import {
    formatMonthDayYearHoursMinutes,
    formatMonthDayYearHoursMinutesSeconds,
} from '../../../common/helpers/utils';
import { useApplicationType } from '../../../common/hooks/use-application-type';
import React from 'react';
import { TabSection } from '../tabs/tab-section';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features';
import { ChangeViewDisplayName } from '../../../fr-event-details-page/types';

const formatValue = (value?: string) => {
    if (value) {
        try {
            // Check if this is a date by parsing it 2 ways and computing its epoch
            // This works because parseISO and new Date will return different things
            // if the provided value is not actually a full ISO string
            if (parseISO(value).getTime() === new Date(value).getTime()) {
                return formatMonthDayYearHoursMinutes(value);
            } else {
                return value;
            }
        } catch (e) {
            return value;
        }
    }

    return value;
};

export function EntityLog({
    actions,
    isLoading,
}: {
    actions?: ElasticUserAction[];
    isLoading: boolean;
}) {
    const { isFirstResponder } = useApplicationType();
    const { additionalDispositionsEnabled } = useEnabledFeatures();

    return (
        <TabSection loading={isLoading} constrainToParentHeight={true} testId="entity-log-section">
            {!!actions?.length && (
                <Virtuoso
                    style={{ paddingRight: '8px' }}
                    totalCount={actions.length}
                    data={actions}
                    itemContent={(index) => {
                        const event = actions[index];
                        const isHighPriorityUserActionTypes =
                            event.actionType ===
                                ActionTypeEnum.EVENT_ADD_NARRATIVE_HIGH_PRIORITY.name ||
                            event.actionType === ActionTypeEnum.DEX_HIT_RETURNED_FOR_EVENT.name;

                        return (
                            <Box
                                // Add vertical spacing between each row
                                // We can't just wrap the list in a stack because this
                                // adds margins that virtuoso does not account properly
                                paddingTop={index > 0 ? cssVar('arc.space.4') : 0}
                                paddingRight={isFirstResponder ? cssVar('arc.space.2') : 0}
                            >
                                <Stack key={event.id} spacing={1}>
                                    <Stack
                                        justifyContent="space-between"
                                        direction="row"
                                        style={
                                            isHighPriorityUserActionTypes
                                                ? {
                                                      backgroundColor: 'var(--arc-colors-urgent-default)',
                                                  }
                                                : undefined
                                        }
                                    >
                                        <Text
                                            fontSize="sm"
                                            style={{
                                                whiteSpace: 'pre-wrap',
                                                color: isHighPriorityUserActionTypes
                                                    ? 'var(--arc-colors-urgent-content)'
                                                    : cssVar('arc.colors.text.primary'),
                                            }}
                                        >
                                            {event.eventLogMessage}
                                        </Text>
                                        <Text
                                            variant="caption"
                                            fontFamily="mono"
                                            style={{
                                                color: isHighPriorityUserActionTypes
                                                    ? 'var(--arc-colors-urgent-content)'
                                                    : cssVar('arc.colors.text.tertiary'),
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {formatMonthDayYearHoursMinutesSeconds(
                                                event.createdDateUtc
                                            )}
                                        </Text>
                                    </Stack>
                                    {event.changeViews.length && (
                                        <DataList
                                            spacing={2}
                                            style={{
                                                padding: cssVar('arc.space.2'),
                                                backgroundColor: isFirstResponder
                                                    ? cssVar('arc.colors.raw.whiteAlpha.300')
                                                    : cssVar('arc.colors.surface.foreground'),
                                                borderRadius: cssVar('arc.radii.md'),
                                            }}
                                        >
                                            {event.changeViews.map((changeView) => {
                                                const formattedOldValue = formatValue(
                                                    changeView.oldValue
                                                );
                                                const formattedNewValue = formatValue(
                                                    changeView.newValue
                                                );
                                                if (
                                                    isFirstResponder &&
                                                    !additionalDispositionsEnabled &&
                                                    changeView.displayName ===
                                                        ChangeViewDisplayName.AdditionalDisposition
                                                ) {
                                                    return null;
                                                }
                                                return (
                                                    <DataItem
                                                        key={changeView.displayName}
                                                        dataKey={changeView.displayName}
                                                    >
                                                        {formattedOldValue && (
                                                            <Text
                                                                color="tertiary"
                                                                style={{
                                                                    minWidth: 0,
                                                                    textDecoration: 'line-through',
                                                                }}
                                                            >
                                                                {formattedOldValue}
                                                            </Text>
                                                        )}
                                                        <Text style={{ minWidth: 0 }}>
                                                            {formattedNewValue}
                                                        </Text>
                                                    </DataItem>
                                                );
                                            })}
                                        </DataList>
                                    )}
                                </Stack>
                            </Box>
                        );
                    }}
                />
            )}
        </TabSection>
    );
}
